// REST зоны cli: слэш-команды, `!`-режим, «@»-пути и вложения (README §2а,
// §2в, ui-plan пп.8–9). Отдельно от api.ts (его ведёт интегратор): здесь же
// загрузка с прогрессом — fetch его не умеет, нужен XMLHttpRequest.

import { getToken } from '../api'
import type {
  AttachmentLimits, AttachmentResponse, CommandInfo, CommandResult, FileEntry,
} from '../protocol'

const TIMEOUT_MS = 30_000

export class CliApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

/** FastAPI отдаёт `{"detail": "…"}` — в интерфейс идёт только причина. */
export function detailOf(raw: string, status: number): string {
  const text = raw.trim()
  if (!text) return `ошибка ${status}`
  if (!text.startsWith('{')) return text
  try {
    const parsed = JSON.parse(text) as { detail?: unknown }
    if (typeof parsed.detail === 'string') return parsed.detail
  } catch { /* не JSON */ }
  return text
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const token = getToken()
  if (token) headers.set('X-Agentbox-Token', token)
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const abort = new AbortController()
  const timer = window.setTimeout(() => abort.abort(), TIMEOUT_MS)
  let res: Response
  try {
    res = await fetch(`/api${path}`, { ...init, headers, credentials: 'same-origin', signal: abort.signal })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new CliApiError(408, 'сервер не ответил вовремя')
    }
    throw new CliApiError(0, 'нет связи с сервером')
  } finally {
    window.clearTimeout(timer)
  }
  if (!res.ok) throw new CliApiError(res.status, detailOf(await res.text().catch(() => ''), res.status))
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const base = (id: string) => `/sessions/${encodeURIComponent(id)}`

export const cliApi = {
  commands: (id: string) => call<CommandInfo[]>(`${base(id)}/commands`),
  command: (id: string, name: string, args: string) =>
    call<CommandResult>(`${base(id)}/command`, { method: 'POST', body: JSON.stringify({ name, args }) }),
  bash: (id: string, command: string) =>
    call<{ seq: number }>(`${base(id)}/bash`, { method: 'POST', body: JSON.stringify({ command }) }),
  files: (id: string, q: string) =>
    call<FileEntry[]>(`${base(id)}/files?q=${encodeURIComponent(q)}&limit=30`),
  limits: (id: string) => call<AttachmentLimits>(`${base(id)}/attachments`),
  interrupt: (id: string) => call<void>(`${base(id)}/interrupt`, { method: 'POST' }),

  /** Загрузка вложения с прогрессом. `abort()` у возвращённого — отмена. */
  upload(
    id: string, file: File, onProgress: (share: number) => void,
  ): { done: Promise<AttachmentResponse>; abort: () => void } {
    const xhr = new XMLHttpRequest()
    const done = new Promise<AttachmentResponse>((resolve, reject) => {
      xhr.open('POST', `/api${base(id)}/attachments`)
      const token = getToken()
      if (token) xhr.setRequestHeader('X-Agentbox-Token', token)
      xhr.withCredentials = true
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && ev.total > 0) onProgress(ev.loaded / ev.total)
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as AttachmentResponse)
          } catch {
            reject(new CliApiError(xhr.status, 'сервер ответил непонятно'))
          }
        } else {
          reject(new CliApiError(xhr.status, detailOf(xhr.responseText, xhr.status)))
        }
      }
      xhr.onerror = () => reject(new CliApiError(0, 'нет связи с сервером'))
      xhr.onabort = () => reject(new CliApiError(-1, 'отменено'))
      const form = new FormData()
      form.append('file', file, file.name)
      xhr.send(form)
    })
    return { done, abort: () => xhr.abort() }
  },
}

/** Ссылка на вложение (скачивание — только своей сессии, проверяет сервер). */
export function attachmentUrl(sessionId: string, attId: string, inline = false): string {
  return `/api${base(sessionId)}/attachments/${attId}${inline ? '?inline=1' : ''}`
}

/** Вложение как blob: заголовок с токеном работает и там, где cookie нет. */
const blobCache = new Map<string, Promise<string>>()

export function attachmentBlob(sessionId: string, attId: string, inline: boolean): Promise<string> {
  const key = `${sessionId}:${attId}:${inline ? 1 : 0}`
  let hit = blobCache.get(key)
  if (!hit) {
    hit = (async () => {
      const headers = new Headers()
      const token = getToken()
      if (token) headers.set('X-Agentbox-Token', token)
      const res = await fetch(attachmentUrl(sessionId, attId, inline), { headers, credentials: 'same-origin' })
      if (!res.ok) throw new CliApiError(res.status, detailOf(await res.text().catch(() => ''), res.status))
      return URL.createObjectURL(await res.blob())
    })()
    hit.catch(() => blobCache.delete(key))
    blobCache.set(key, hit)
  }
  return hit
}

/** Скачать вложение под его именем. */
export async function downloadAttachment(sessionId: string, attId: string, name: string): Promise<void> {
  const url = await attachmentBlob(sessionId, attId, false)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
}
